import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiUsers, FiCode, FiCloud, FiCpu } from 'react-icons/fi';
import '../../styles/styles.css';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

const services = [
  {
    icon: <FiUsers />,
    title: 'IT Staffing Solutions',
    description: 'Connect with top-tier IT professionals perfectly matched to your project needs and company culture.',
    paragraph: 'Our IT staffing division specializes in bridging the talent gap for technology companies. We employ a multi-stage screening process that evaluates technical competencies, soft skills, and cultural alignment. Whether you need developers, DevOps engineers, data scientists, or IT managers, we deliver candidates who can contribute from day one. Our flexible engagement models include direct hiring, contract staffing, and managed teams tailored to your specific requirements.',
    image: 'staffing.jpg'
  },
  {
    icon: <FiCode />,
    title: 'Software Development',
    description: 'End-to-end software solutions from concept to deployment with agile methodologies.',
    paragraph: 'We build robust, scalable software solutions using cutting-edge technologies and industry best practices. Our development process follows agile methodologies with continuous integration and deployment. From MVP development to enterprise-scale applications, we deliver high-quality code with comprehensive testing and documentation. Our team stays current with the latest frameworks and tools to ensure your solution is future-proof and maintainable.',
    image: 'software-dev.jpg'
  },
  {
    icon: <FiCloud />,
    title: 'Cloud Solutions',
    description: 'AWS cloud architecture, migration, and optimization services for scalable infrastructure.',
    paragraph: 'Transform your infrastructure with our comprehensive cloud services. We design and implement cloud architectures that are secure, scalable, and cost-effective. Our cloud migration services ensure minimal disruption to your operations, while our optimization strategies help reduce costs and improve performance. We also provide ongoing cloud management, monitoring, and security services to keep your cloud environment running smoothly.',
    image: 'cloud-solutions.jpg'
  },
  {
    icon: <FiCpu />,
    title: 'AI & Machine Learning',
    description: 'Implement GenAI and ML solutions to drive innovation and business intelligence.',
    paragraph: 'Unlock new opportunities with our AI and machine learning expertise. We develop custom AI solutions that solve real business challenges, from predictive analytics and natural language processing to computer vision and generative AI. Our data scientists work closely with your team to understand your data landscape and build models that deliver actionable insights. We also help integrate AI capabilities into your existing systems and workflows.',
    image: 'ai-ml.jpg'
  }
];

  const technologies = [
    { name: 'React.js', icon: '⚛️', color: '#61DAFB' },
    { name: 'Python', icon: '🐍', color: '#3776AB' },
    { name: 'Java', icon: '☕', color: '#007396' },
    { name: 'AWS', icon: '☁️', color: '#FF9900' },
    { name: 'GenAI', icon: '🧠', color: '#10B981' },
    { name: 'Node.js', icon: '🟢', color: '#339933' }
  ];

  const stats = [
    { value: '200+', label: 'Projects Completed' },
    { value: '50+', label: 'Expert Developers' },
    { value: '99%', label: 'Client Satisfaction' },
    { value: '24/7', label: 'Support Available' }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % services.length);
      }, 5000); // Change slide every 5 seconds
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, services.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (isAutoPlaying) {
      // Reset the timer when manually navigating
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % services.length);
      }, 5000);
    }
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % services.length);
  };

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + services.length) % services.length);
  };

  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  return (
    <>
      {/* Hero Section */}
      <section id="intro" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="intro-container">
          <div className="carousel-container" ref={carouselRef}>
            {/* Carousel Indicators */}
            <div className="carousel-indicators">
              {services.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            
        {/* Carousel Slides */}
        <div className="carousel-slides">
          {services.map((service, index) => (
            <div
              key={index}
              className={`carousel-slide ${index === currentSlide ? 'active' : ''} ${
                index === (currentSlide - 1 + services.length) % services.length ? 'prev' : ''
              } ${
                index === (currentSlide + 1) % services.length ? 'next' : ''
              }`}
            >
              <div className="carousel-background">
                <img 
                  src={require(`../../images/${service.image}`)} 
                  alt={service.title}
                  className="carousel-image"
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    // Simple error handling without fallback images
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="carousel-content-wrapper">
                <div className="carousel-content">
                  <h2 style={{ color: '#00e7ff' }}>{service.title}</h2>
                  <p style={{ color: '#ffffffff' }} className="carousel-description">{service.description}</p>
                  {/* <p className="carousel-paragraph">{service.paragraph}</p> */}
                  <Link to="/services" className="btn-get-started scrollto">Get Started</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
            
            {/* Carousel Controls */}
            <button className="carousel-control prev" onClick={prevSlide} aria-label="Previous slide">
              <span className="carousel-control-icon">‹</span>
            </button>
            <button className="carousel-control next" onClick={nextSlide} aria-label="Next slide">
              <span className="carousel-control-icon">›</span>
            </button>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="section tech-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" style={{ color: '#00e7ff' }}>Core Technologies</h2>
            <p className="section-subtitle">
              Expertise across modern and emerging technology stacks
            </p>
          </div>
          <div className="tech-grid">
            {technologies.map((tech, index) => (
              <div key={index} className="tech-card">
                <div className="tech-icon" style={{ color: tech.color, fontSize: '2.5rem' }}>
                  {tech.icon}
                </div>
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

            {/* Services Section */}
      <section className="section services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" style={{ color: '#00e7ff' }}>Our Services</h2>
            <p className="section-subtitle">
              Comprehensive IT solutions tailored to your business needs
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {service.icon}
                </div>
                <h3 className="service-title" style={{ color: '#00e7ff' }}>{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <p className="service-paragraph">{service.paragraph}</p>
                <Link to="/services" className="service-link">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/services" className="btn btn-secondary">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div>
          <div className="cta-content">
            <h2 style={{ color: '#00e7ff' }}>Ready to Transform Your IT Capabilities?</h2>
            <p>
              Partner with vinfocloud for innovative solutions that drive growth 
              and technological excellence.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get Started
              </Link>
              <Link to="/about" className="btn btn-outline">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;